import { createGlobalStyle } from '@xstyled/styled-components';
import blueJones from '~/assets/fonts/blue-jones.woff2';
import raleway from '~/assets/fonts/Raleway-Regular.woff2';
import recoleta from '~/assets/fonts/Recoleta-Regular.woff2';
import ralewayBold from '~/assets/fonts/Raleway-Bold.woff2';
import recoletaBold from '~/assets/fonts/Recoleta-SemiBold.woff2';
import plusJakartaSans from '~/assets/fonts/PlusJakartaSans.woff2';
import plusJakartaSansExt from '~/assets/fonts/PlusJakartaSans-ext.woff2';
import { FIELD_CUE_COLORS } from '~/lib/themes/fieldCueColors';

export const DarkStyle = createGlobalStyle`
  @font-face {
    font-family: bluejones;
    src: url(${blueJones});
  }

  @font-face {
    font-family: raleway;
    src: url(${raleway});
  }

  @font-face {
    font-family: recoleta;
    src: url(${recoleta});
  }

  @font-face {
    font-family: ralewayBold;
    src: url(${ralewayBold});
  }

  @font-face {
    font-family: recoletaBold;
    src: url(${recoletaBold});
  }

  @font-face {
    font-family: 'Plus Jakarta Sans';
    font-style: normal;
    font-weight: 200 800;
    font-display: swap;
    src: url(${plusJakartaSans}) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }

  @font-face {
    font-family: 'Plus Jakarta Sans';
    font-style: normal;
    font-weight: 200 800;
    font-display: swap;
    src: url(${plusJakartaSansExt}) format('woff2');
    unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }

  :root {
    --color-black: #EFF0F1;
    --color-white: #1A171C;

    --color-grey-dk: #EFEFEF;
    --color-grey-md: #434343;
    --color-grey-lt: #313131;
    /* CAT-1241: row hover — greyDark stroke (#EFEFEF) still ≥ 3:1 */
    --color-row-hover: #313131;

    --color-accent-dk: #5D07C4;
    --color-accent-md: #9D76FB;
    --color-accent-lt: #5D07C4;

    /* CAT-1261: companions for brand fill / focus / links (overridden in JS) */
    --color-on-accent: #ffffff;
    /* Text CTAs (1.4.3); overridden by applyBrandContrastVars when brand is light. */
    --color-on-accent-text: #ffffff;
    --color-accent-fg: var(--color-accent-dk);
    --color-accent-on-light: var(--color-accent-dk);
    --color-focus-ring: var(--color-accent-dk);
    --color-focus-ring-inner: #ffffff;
    --color-focus-ring-hotspot: var(--color-accent-dk);
    --color-accent-bullet: var(--color-accent-dk);
    --color-on-accent-bullet: #ffffff;
    --color-accent-edge: transparent;
    /* ENG-13020: editor cue for an empty optional field (≥ 4.5:1). */
    --color-field-cue: ${FIELD_CUE_COLORS.dark};
    --color-field-cue-on-light: ${FIELD_CUE_COLORS.onLight};
    --color-field-cue-on-dark: ${FIELD_CUE_COLORS.onDark};
    --color-field-cue-on-tint: ${FIELD_CUE_COLORS.onTint};
    --color-modal-edge: transparent;

    --color-yellow-dk: #FDD15F;
    --color-yellow-md: #F8EAB7;
    --color-yellow-lt: #E6A601;

    --color-green-dk: #00A876;
    --color-green-md: #92E8BF;
    --color-green-lt: #E8FAED;

    --color-red-dk: #F7326D;
    --color-red-md: #FABCCB;
    --color-red-lt: #FDF2F4;

    --color-blue-md: #0969FD;

    --color-pink-lt: #0D0025;
    --color-pink-lt2: #15002E;

    --title-font: recoleta;
    --body-font: raleway;
    --llama-font: bluejones;

    /* Scroll track transparent; thumb readable on dark surfaces */
    --scrollbar-track: transparent;
    --scrollbar-thumb: #6f6f6f;
  }

  /* Firefox — same EDS scrollbar as MainStyle (DarkStyle replaces it) */
  * {
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb) transparent;
  }

  /* Chrome, Safari, Edge — track must stay transparent; otherwise a light
     Preflight/body shows through the gutter as a white strip. */
  *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background: transparent;
  }

  *::-webkit-scrollbar-track,
  *::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb);
    border-radius: 999px;
    border: 1px solid transparent;
    background-clip: content-box;
  }

  *::-webkit-scrollbar-corner {
    background: transparent;
  }

  html,
  body,
  .root,
  #root {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    /* Dark page canvas so a transparent scrollbar track does not flash white */
    background-color: var(--color-white);
  }

  a {
    text-decoration: none;
    color: var(--color-accent-fg) !important;
  }

  p {
    margin: 0;
  }

  button {
    cursor: pointer;
  }

  /* UA button:focus has higher specificity than bare :focus-visible;
     target element selectors so primary outline wins over the blue ring. */
  a:focus,
  button:focus,
  input:focus,
  textarea:focus,
  select:focus,
  summary:focus,
  [tabindex]:focus {
    outline: none;
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  summary:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  textarea {
    border: none;
    background: var(--color-grey-lt);
    border-radius: md;
  }

  video::-webkit-media-controls {
    display: none;
  }

  .emailStyleForDarkTheme, .envelopeStyleForDarkTheme {
    background-color: #4B4B4B;
  }

  .cardStyleForDarkTheme {
    border: 1px solid #515151;
    box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
  }

  .logoStyleForDarkTheme {
    padding: 2px 5px;
    background-color: #FFFFFF;
    border-radius: 8px;
  }

  .invertStyleForDarkMode {
    filter: invert(93%);
  }

  .itemStyleForDarkTheme {
    color: #FFFFFF !important;
  }

  /* Figma EDS-CAT 421:24330 — captions on black in dark mode */
  .CaptionBox {
    background: #000000 !important;
    color: #ffffff !important;
  }

  /* Figma EDS-CAT 649:5206 — Complete the steps chip: 50% white border */
  .CompleteStepsPulse {
    border-color: rgba(255, 255, 255, 0.5) !important;
  }

  .subtitleStyleForDarkTheme {
    padding: 5px 8px;
    width: fit-content;
    background-color: #FFFFFF;
    border-radius: 8px;
    /* Pill is always light — brand-as-fill would vanish when brand is white. */
    color: var(--color-accent-on-light);

    svg {
      position: relative;
    }
  }

  .modalStyleForDarkTheme {
    -webkit-border-radius: 20px;
    -moz-border-radius: 20px;
    border-radius: 20px;

    -webkit-box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
    -moz-box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
    box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
  }

  .navigationStyleForDarkTheme {
    -webkit-box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
    -moz-box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
    box-shadow: rgb(255 255 255 / 20%) 0px 12px 28px 0px, rgb(255 255 255 / 10%) 0px 2px 4px 0px, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset;
    background-color: var(--color-white);
  }

  .brightnessStyleForDarkTheme {
    filter: brightness(0.7);
  }

  .pointerStyleForDarkTheme {
    filter: invert(93%);
  }

  /* Timer pill uses same EDS chrome in dark (Figma 854:4022) — white on rgba(46,46,46,0.6). */
  .timeRemainingPillStyleForDarkTheme {
    color: #ffffff;
    background: rgba(46, 46, 46, 0.6);
  }

  .textareaStyleForDarTheme {
    color: #FFFFFF;
    border: 1px solid var(--color-grey-dk);
    ::placeholder {
      color: #FFFFFF;
      opacity: 1;
    }
    :-ms-input-placeholder {
      color: #FFFFFF;
    }

    ::-ms-input-placeholder {
      color: #FFFFFF;
    }
  }

  .cardBorderStyleForDarkTheme {
    background-color: #FFFFFF !important;
    color: #000000;
  }

  .bgImage, .emptyBgImage {
    filter: brightness(45%);
  }

  .textMessageDarkTheme {
    a {
      color: #FDEF66 !important;
    }
  }

  /* Chatbot dark theme overrides */
  .chatMessageRoot {
    color: #efefef;
  }
  .chatMessageRoot article {
    background-color: transparent;
    color: #efefef;
    border: 1px solid #515151;
  }
  .chatMessageRoot article:before { display: none; }
  .chatMessageRoot code { background: #2d2d2d; color: #f5f5f5; }
  .chatMessageRoot blockquote { background: #1f1f1f; border-left-color: #6a6a6a; }

  /* Differentiate outgoing (user) vs incoming (assistant) in dark mode */
  .chatMessageRoot li[data-type='outgoing'] article {
    background-color: #2f2f2f; /* subtle filled gray */
    color: #ffffff;
    border: 1px solid #3a3a3a;
  }
  .chatMessageRoot li[data-type='outgoing'] article:before {
    display: block;
    border-left-color: #2f2f2f !important;
  }

  /* Incoming (assistant) bubble: transparent with border and left notch */
  .chatMessageRoot li[data-type='incoming'] article {
    background-color: transparent;
    color: #efefef;
    border: 1px solid #515151;
  }
  .chatMessageRoot li[data-type='incoming'] article:before {
    display: block;
    border-right-color: #515151 !important; /* outer border triangle */
    z-index: 1;
    left: -15.5px;
  }
  .chatMessageRoot li[data-type='incoming'] article:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: -12px;
    width: 36px;
    height: 26px;
    border-right: 22px solid var(--color-white); /* inner fill equals container bg */
    border-bottom-right-radius: 100%;
    transform: rotate(-4deg) scale(1.2);
    z-index: 1;
    pointer-events: none;
  }

  /* Safari-specific: hide assistant notch to avoid anti-alias artifacts */
  @supports (-webkit-touch-callout: none) {
    .chatMessageRoot li[data-type='incoming'] article:before,
    .chatMessageRoot li[data-type='incoming'] article:after {
      display: none !important;
    }
  }
  ._AccordionContentBox {
    p {
      color: #000 !important;
    }
    color: #000;
  }

  .text-align-left {
    text-align: left !important;
  }

  .text-align-center {
    text-align: center !important;
  }

  .text-align-right {
    text-align: right !important;
  }

  .text-align-justify {
    text-align: justify !important;
  }
`;
